import { Menu, Search, Bell, ChevronLeft, Command } from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { handleGetNotifications, handleMarkAllNotificationsRead, handleMarkNotificationRead } from '../../shared/controllers/notificationController';
import { unwrapApiArray } from '../../shared/utils/apiResponse';
import { formatDateTime } from '../../shared/utils/dateTime';
import { useSidebar, useAuth } from '../app/providers';

import GlobalSearch from './GlobalSearch';

// Map paths to readable titles
const pathTitles: Record<string, string> = {
    '/admin': 'Dashboard',
    '/admin/users': 'Users',
    '/admin/organizer-management': 'Organizer Management',
    '/admin/organizers': 'Organizer Management',
    '/admin/saca-pro': 'Saca Pro',
    '/admin/referrals': 'Referrals',
    '/admin/referral-rewards': 'Referral Rewards',
    '/admin/games': 'Games',
    '/admin/free-games': 'Free Games',
    '/admin/game-approvals': 'Game Approvals',
    '/admin/game-payouts': 'Game Payouts',
    '/admin/pitches': 'Pitches',
    '/admin/pitch-approvals': 'Pitch Approvals',
    '/admin/bookings': 'Bookings',
    '/admin/leagues': 'Leagues',
    '/admin/events': 'Events',
    '/admin/transactions': 'Transactions',
    '/admin/plans': 'Plans',
    '/admin/blogs': 'Blog',
    '/admin/charity': 'Charity',
    '/admin/equipment': 'Equipment',
    '/admin/settings': 'Settings',
    '/admin/locations': 'Locations',
    '/admin/notifications': 'Notifications',
    '/admin/projects': 'Projects',
};

interface AdminNotificationItem {
    id: string | number;
    title: string;
    message: string;
    created_at?: string;
    read_at?: string | null;
}

interface NotificationSubmitter {
    id?: number | string;
    name?: string;
    username?: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const parseSubmitter = (value: unknown): NotificationSubmitter | undefined => {
    const record = asRecord(value);
    if (Object.keys(record).length === 0) return undefined;

    const submitter: NotificationSubmitter = {};
    if (record.id !== undefined && record.id !== null) {
        submitter.id = record.id as number | string;
    }
    if (typeof record.name === 'string') {
        submitter.name = record.name;
    }
    if (typeof record.username === 'string') {
        submitter.username = record.username;
    }

    return Object.keys(submitter).length > 0 ? submitter : undefined;
};

const resolveSubmitterName = (submitter?: NotificationSubmitter): string | null => {
    if (!submitter) return null;
    if (submitter.name && submitter.name.trim()) return submitter.name.trim();
    if (submitter.username && submitter.username.trim()) return `@${submitter.username.trim().replace(/^@/, '')}`;
    if (submitter.id !== undefined && submitter.id !== null) return `User #${submitter.id}`;
    return null;
};

const Header: React.FC = () => {
    const { toggleMobile, isCollapsed } = useSidebar();
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [notifications, setNotifications] = useState<AdminNotificationItem[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const notificationPanelRef = useRef<HTMLDivElement | null>(null);

    // Get current page title
    const getPageTitle = () => {
        const basePath = '/' + location.pathname.split('/').slice(0, 3).join('/').replace(/^\//, '');
        return pathTitles[basePath] || 'Dashboard';
    };

    // Check if we're on a detail page (has an ID in the URL)
    const isDetailPage = location.pathname.split('/').length > 3;
    const pageTitle = getPageTitle();

    // Keyboard shortcut for search
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsSearchOpen(true);
                return;
            }

            if (e.key === 'Escape') {
                setIsSearchOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Ensure stale overlay state does not survive route changes.
    useEffect(() => {
        setIsSearchOpen(false);
        setIsNotificationsOpen(false);
    }, [location.pathname, location.search]);

    const refreshNotifications = useCallback(async (showLoading = false) => {
        if (showLoading) {
            setLoadingNotifications(true);
        }

        try {
            const response = await handleGetNotifications<Record<string, unknown>>('admin', {
                per_page: 20,
                page: 1,
            });

            const rawNotifications = unwrapApiArray<Record<string, unknown>>(response);
            const mappedNotifications: AdminNotificationItem[] = rawNotifications
                .map((item) => {
                    const itemId = item.id;
                    if (typeof itemId !== 'string' && typeof itemId !== 'number') {
                        return null;
                    }

                    const payload = (typeof item.data === 'object' && item.data !== null)
                        ? (item.data as Record<string, unknown>)
                        : {};
                    const submitter = parseSubmitter(payload.submitted_by);
                    const submitterName = resolveSubmitterName(submitter);
                    const title =
                        (typeof item.title === 'string' && item.title.trim()) ||
                        (typeof payload.title === 'string' && payload.title.trim()) ||
                        'Notification';
                    let message =
                        (typeof item.message === 'string' && item.message.trim()) ||
                        (typeof item.body === 'string' && item.body.trim()) ||
                        (typeof payload.description === 'string' && payload.description.trim()) ||
                        (typeof payload.message === 'string' && payload.message.trim()) ||
                        'You have a new update.';

                    if (submitterName && !message.toLowerCase().includes(submitterName.toLowerCase())) {
                        message = `${message} Submitted by ${submitterName}.`;
                    }

                    return {
                        id: itemId,
                        title,
                        message,
                        ...(typeof item.created_at === 'string' ? { created_at: item.created_at } : {}),
                        ...((typeof item.read_at === 'string' || item.read_at === null)
                            ? { read_at: item.read_at as string | null }
                            : {}),
                    };
                })
                .filter((item): item is AdminNotificationItem => item !== null);

            const unread = mappedNotifications.filter(
                (item) => item.read_at === null || item.read_at === undefined
            ).length;

            setNotifications(mappedNotifications);
            setUnreadNotifications(unread);
        } catch (error) {
            console.error('Failed to fetch admin notifications:', error);
            setNotifications([]);
            setUnreadNotifications(0);
        } finally {
            if (showLoading) {
                setLoadingNotifications(false);
            }
        }
    }, []);

    const markNotificationAsRead = useCallback(async (id: string | number, isUnread: boolean) => {
        if (!isUnread) {
            return;
        }

        try {
            await handleMarkNotificationRead('admin', String(id));
            await refreshNotifications();
        } catch (error) {
            console.error('Failed to mark admin notification as read:', error);
        }
    }, [refreshNotifications]);

    const markAllNotificationsAsRead = useCallback(async () => {
        try {
            await handleMarkAllNotificationsRead('admin');
            await refreshNotifications();
        } catch (error) {
            console.error('Failed to mark all admin notifications as read:', error);
        }
    }, [refreshNotifications]);

    useEffect(() => {
        void refreshNotifications();
        const interval = globalThis.setInterval(() => {
            void refreshNotifications();
        }, 60000);

        return () => globalThis.clearInterval(interval);
    }, [refreshNotifications]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (!notificationPanelRef.current) {
                return;
            }

            const target = event.target;
            if (target instanceof Node && !notificationPanelRef.current.contains(target)) {
                setIsNotificationsOpen(false);
            }
        };

        if (isNotificationsOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
            return () => document.removeEventListener('mousedown', handleOutsideClick);
        }

        return undefined;
    }, [isNotificationsOpen]);

    useEffect(() => {
        if (isNotificationsOpen) {
            void refreshNotifications(true);
        }
    }, [isNotificationsOpen, refreshNotifications]);

    return (
        <>
            <header
                className={`
          fixed top-0 right-0 z-30 h-16 bg-white border-b border-slate-200
          transition-all duration-300
          ${isCollapsed ? 'lg:left-[72px]' : 'lg:left-64'}
          left-0
        `}
            >
                <div className="flex items-center justify-between h-full px-4 lg:px-6">
                    {/* Left side */}
                    <div className="flex items-center gap-4">
                        {/* Mobile menu button */}
                        <button
                            onClick={toggleMobile}
                            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-600"
                        >
                            <Menu size={20} />
                        </button>

                        {/* Back button for detail pages */}
                        {isDetailPage && (
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center gap-1 text-primary hover:text-primary-dark font-medium text-sm"
                            >
                                <ChevronLeft size={18} />
                                <span>Back</span>
                            </button>
                        )}

                        {/* Page title */}
                        <div>
                            {pageTitle === 'Dashboard' ? (
                                <h1 className="text-lg lg:text-xl font-bold text-slate-900">
                                    Welcome Back, Admin!
                                </h1>
                            ) : (
                                <h1 className="text-lg lg:text-xl font-bold text-slate-900">
                                    {pageTitle}
                                </h1>
                            )}
                        </div>
                    </div>

                    {/* Right side */}
                    <div className="flex items-center gap-2 lg:gap-3">
                        {/* Search button */}
                        <button
                            onClick={() => setIsSearchOpen(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                        >
                            <Search size={16} />
                            <span className="hidden md:inline">Search...</span>
                            <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-white rounded border border-slate-200">
                                <Command size={10} />K
                            </kbd>
                        </button>

                        {/* Notifications */}
                        <div className="relative" ref={notificationPanelRef}>
                            <button
                                onClick={() => setIsNotificationsOpen((prev) => !prev)}
                                className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
                                aria-label="Open notifications"
                            >
                                <Bell size={20} />
                                {unreadNotifications > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center border border-white">
                                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                                    </span>
                                )}
                            </button>

                            {isNotificationsOpen && (
                                <div className="absolute right-0 mt-2 w-96 max-h-[28rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl z-40">
                                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                                        <p className="text-sm font-semibold text-slate-900">Notifications</p>
                                        <div className="flex items-center gap-3">
                                            {unreadNotifications > 0 && (
                                                <button
                                                    onClick={() => {
                                                        void markAllNotificationsAsRead();
                                                    }}
                                                    className="text-xs font-medium text-primary hover:text-primary-dark"
                                                >
                                                    Mark all read
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setIsNotificationsOpen(false);
                                                    navigate('/admin/notifications');
                                                }}
                                                className="text-xs font-medium text-slate-600 hover:text-slate-800"
                                            >
                                                View all
                                            </button>
                                        </div>
                                    </div>

                                    {loadingNotifications ? (
                                        <div className="px-4 py-6 text-sm text-slate-500">Loading...</div>
                                    ) : notifications.length === 0 ? (
                                        <div className="px-4 py-6 text-sm text-slate-500">No notifications yet.</div>
                                    ) : (
                                        <div className="max-h-96 overflow-y-auto">
                                            {notifications.slice(0, 8).map((item) => {
                                                const isUnread = item.read_at === null || item.read_at === undefined;

                                                return (
                                                    <button
                                                        key={String(item.id)}
                                                        onClick={() => {
                                                            void markNotificationAsRead(item.id, isUnread);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${isUnread ? 'bg-emerald-50/30' : 'bg-white'
                                                            }`}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            {isUnread && <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500" />}
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-sm font-medium text-slate-900 truncate">{item.title}</p>
                                                                <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{item.message}</p>
                                                                <p className="text-[11px] text-slate-400 mt-1">
                                                                    {formatDateTime(item.created_at, undefined, '')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* User avatar */}
                        <div className="flex items-center gap-2 pl-2 border-l border-slate-200">
                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-sm">
                                {user?.role?.[0]?.toUpperCase() || 'A'}
                            </div>
                            <span className="hidden md:inline text-sm font-medium text-slate-700">
                                {user?.role || 'Admin'}
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Global search modal */}
            <GlobalSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
        </>
    );
};

export default Header;
